import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BrandMark } from "@/components/workspace/brand-mark";
import { APP_SHELL, MODULE_RAILS } from "@/lib/constants/workspace-layout";
import { cn } from "@/lib/utils/cn";
import { SPACING } from "../../config/layout";
import { SECTION_SUBTITLE } from "../../config/styles";
import Activity from "lucide-react/dist/esm/icons/activity.js";
import AlertTriangle from "lucide-react/dist/esm/icons/alert-triangle.js";
import BarChart3 from "lucide-react/dist/esm/icons/bar-chart-3.js";
import Bell from "lucide-react/dist/esm/icons/bell.js";
import BookOpen from "lucide-react/dist/esm/icons/book-open.js";
import Bot from "lucide-react/dist/esm/icons/bot.js";
import Brain from "lucide-react/dist/esm/icons/brain.js";
import Building2 from "lucide-react/dist/esm/icons/building-2.js";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down.js";
import ChevronLeft from "lucide-react/dist/esm/icons/chevron-left.js";
import CircleHelp from "lucide-react/dist/esm/icons/circle-help.js";
import ClipboardCheck from "lucide-react/dist/esm/icons/clipboard-check.js";
import Cpu from "lucide-react/dist/esm/icons/cpu.js";
import Database from "lucide-react/dist/esm/icons/database.js";
import FileText from "lucide-react/dist/esm/icons/file-text.js";
import FolderSearch from "lucide-react/dist/esm/icons/folder-search.js";
import Handshake from "lucide-react/dist/esm/icons/handshake.js";
import HardDrive from "lucide-react/dist/esm/icons/hard-drive.js";
import Home from "lucide-react/dist/esm/icons/home.js";
import LayoutDashboard from "lucide-react/dist/esm/icons/layout-dashboard.js";
import ListChecks from "lucide-react/dist/esm/icons/list-checks.js";
import ListTree from "lucide-react/dist/esm/icons/list-tree.js";
import Mail from "lucide-react/dist/esm/icons/mail.js";
import MessageCircle from "lucide-react/dist/esm/icons/message-circle.js";
import Network from "lucide-react/dist/esm/icons/network.js";
import Plane from "lucide-react/dist/esm/icons/plane.js";
import Receipt from "lucide-react/dist/esm/icons/receipt.js";
import RefreshCw from "lucide-react/dist/esm/icons/refresh-cw.js";
import Rocket from "lucide-react/dist/esm/icons/rocket.js";
import Search from "lucide-react/dist/esm/icons/search.js";
import Settings from "lucide-react/dist/esm/icons/settings.js";
import Stethoscope from "lucide-react/dist/esm/icons/stethoscope.js";
import Target from "lucide-react/dist/esm/icons/target.js";
import Truck from "lucide-react/dist/esm/icons/truck.js";
import Upload from "lucide-react/dist/esm/icons/upload.js";
import UserCog from "lucide-react/dist/esm/icons/user-cog.js";
import Users from "lucide-react/dist/esm/icons/users.js";
import Workflow from "lucide-react/dist/esm/icons/workflow.js";
import Zap from "lucide-react/dist/esm/icons/zap.js";
import { useFrontendConfig } from "@/lib/hooks/use-frontend-config";
import { useSecurityContext } from "../../contexts/SecurityContext";
import { getSidebarRoutePolicy } from "../../lib/constants/routes/access-policy";
import {
  PATH_ACTIVE_PEER_EXCLUSIONS,
  ROUTES,
  usesContentCapability,
} from "../../lib/constants/routes/route-paths";
import { useTranslation } from "@/lib/hooks/use-translation";
import { localizeNavigationItems, type LocalizableNavigationItem } from "@/lib/i18n/localize-navigation";
import { resolveNavSectionLabel } from "@/lib/i18n/resolve-nav-label";
import { createRoutePrefetchHandlers } from "@/lib/chunk-recovery";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface NavigationItem extends LocalizableNavigationItem {
  icon: string;
  hrmSubgroup?: string;
  navModule?: string;
}

const MODULE_SECTION_ORDER = [
  "conversations",
  "party-master",
  "supplier-ops",
  "partner-ops",
  "intake",
  "customer",
  "work",
  "strategy",
  "contracts",
  "finance",
  "hrm",
  "policy-rules",
  "ai-safety",
  "template-governance",
  "platform-administration",
  "context-governance",
  "templates",
  "rules",
  "workspace",
  "administration",
] as const;
const MODULE_SECTION_LANDING_PATHS: Record<string, string> = {
  strategy: ROUTES.STRATEGY,
  finance: ROUTES.FINANCE_INVOICES,
  hrm: ROUTES.HRM_LEGAL_ENTITIES,
  conversations: ROUTES.CONVERSATIONS,
  "party-master": ROUTES.PARTIES,
  "supplier-ops": ROUTES.SUPPLIER_DIRECTORY,
  "partner-ops": ROUTES.PARTNER_DIRECTORY,
};

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Home,
  Bot,
  Search,
  Users,
  BarChart3,
  Database,
  Settings,
  Activity,
  Rocket,
  Brain,
  Zap,
  MessageCircle,
  Plane,
  BookOpen,
  Mail,
  Workflow,
  Bell,
  UserCog,
  FileText,
  Stethoscope,
  Network,
  ListTree,
  RefreshCw,
  Building2,
  Cpu,
  Receipt,
  AlertTriangle,
  Upload,
  ClipboardCheck,
  LayoutDashboard,
  FolderSearch,
  HardDrive,
  ListChecks,
  Target,
  Truck,
  Handshake,
};

const UNKNOWN_NAV_ICON = CircleHelp;

const getIcon = (iconName: string): React.ComponentType<{ className?: string }> => {
  const icon = iconMap[iconName];
  if (icon) {
    return icon;
  }
  if (import.meta.env.DEV) {
    console.warn(`[Sidebar] unknown navigation icon: ${iconName}`);
  }
  return UNKNOWN_NAV_ICON;
};

function configText(value: string | undefined, defaultValue: string): string {
  if (typeof value !== "string") return defaultValue;
  const cleaned = value.trim();
  if (cleaned.length === 0) return defaultValue;
  return cleaned;
}

interface NavigationItemProps {
  item: NavigationItem;
  collapsed: boolean;
  isActive: boolean;
  hasActiveChild: boolean;
  isOpen: boolean;
  currentPath: string;
  onOpen: (itemId: string) => void;
}

function isPathActive(currentPath: string, itemPath: string): boolean {
  if (currentPath === itemPath) return true;
  if (!currentPath.startsWith(`${itemPath}/`)) return false;
  const excludedPeers = PATH_ACTIVE_PEER_EXCLUSIONS[itemPath];
  if (excludedPeers !== undefined) {
    for (const peerPath of excludedPeers) {
      if (currentPath === peerPath || currentPath.startsWith(`${peerPath}/`)) return false;
    }
  }
  return true;
}

function navChildrenUseModuleSections(children: NavigationItem[] | undefined): boolean {
  const list = children ?? [];
  if (list.length === 0) return false;
  const sections = new Set(
    list.map((child) => child.section).filter((section): section is string => Boolean(section)),
  );
  if (sections.size === 0) return false;
  // Parties (multi) + Finance (single): all leaves carry section → module-section UI.
  return list.every((child) => Boolean(child.section));
}

function sidebarRootChildren(children: NavigationItem[]): NavigationItem[] {
  return children.filter((child) => !child.parentPath);
}

function moduleSectionId(section: string | undefined): string | undefined {
  return section;
}

function rootModuleChildren(children: NavigationItem[], section: string): NavigationItem[] {
  return children.filter((child) => moduleSectionId(child.section) === section && !child.parentPath);
}

function sectionLandingItem(section: string, roots: NavigationItem[]): NavigationItem | null {
  if (roots.length === 0) return null;
  const landingPath = MODULE_SECTION_LANDING_PATHS[section];
  if (landingPath !== undefined) {
    const preferred = roots.find((item) => item.path === landingPath);
    if (preferred !== undefined) return preferred;
  }
  return roots[0] ?? null;
}

function childModuleChildren(children: NavigationItem[], parentPath: string): NavigationItem[] {
  return children.filter((child) => child.parentPath === parentPath);
}

function visibleSectionItems(children: NavigationItem[], section: string, landingItem: NavigationItem | null): NavigationItem[] {
  const roots = rootModuleChildren(children, section);
  // Keep section roots as peers (consultation/directory stay discoverable leaves).
  // Nested items under landing path still append when present (finance/strategy pattern).
  if (landingItem === null) return roots;
  const nested = childModuleChildren(children, landingItem.path);
  if (nested.length === 0) return roots;
  const rootIds = new Set(roots.map((item) => item.id));
  return [...roots, ...nested.filter((item) => !rootIds.has(item.id))];
}

function sectionIdsForChildren(children: NavigationItem[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const known of MODULE_SECTION_ORDER) {
    if (children.some((child) => moduleSectionId(child.section) === known)) {
      seen.add(known);
      out.push(known);
    }
  }
  for (const child of children) {
    const section = moduleSectionId(child.section);
    if (!section) continue;
    if (seen.has(section)) continue;
    seen.add(section);
    out.push(section);
  }
  return out;
}

function moduleSectionContainsPath(children: NavigationItem[], section: string, currentPath: string): boolean {
  return children.some((child) => moduleSectionId(child.section) === section && isPathActive(currentPath, child.path));
}

function activeModuleSectionId(children: NavigationItem[], currentPath: string): string {
  const sectionIds = sectionIdsForChildren(children);
  for (const section of sectionIds) {
    if (children.some((child) => moduleSectionId(child.section) === section && currentPath === child.path)) return section;
  }
  for (const section of sectionIds) {
    if (moduleSectionContainsPath(children, section, currentPath)) return section;
  }
  return sectionIds[0] ?? "";
}

function moduleItemContainsPath(children: NavigationItem[], item: NavigationItem, currentPath: string): boolean {
  if (isPathActive(currentPath, item.path)) return true;
  return childModuleChildren(children, item.path).some((child) => moduleItemContainsPath(children, child, currentPath));
}

interface ModuleNavigationLinkProps {
  children: NavigationItem[];
  item: NavigationItem;
  currentPath: string;
  depth: number;
  /** Base Style leaf tone shared by module-section + sidebar-expand (DEC-P0-FE-MODULE-RAILS-002). */
  railTone?: "default" | "rail";
}

const ModuleNavigationLink: React.FC<ModuleNavigationLinkProps> = ({
  children,
  item,
  currentPath,
  depth,
  railTone = "default",
}) => {
  const nested = childModuleChildren(children, item.path);
  const isBranchActive = moduleItemContainsPath(children, item, currentPath);
  const Icon = getIcon(item.icon);
  const nestedVisible = nested.length > 0 && isBranchActive;
  const useRailTone = railTone === "rail";
  const itemClassName = useRailTone
    ? MODULE_RAILS.SECTION_LEAF
    : depth === 0
      ? "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200"
      : "group flex h-8 items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium transition-all duration-200";
  const iconClassName = useRailTone
    ? MODULE_RAILS.SECTION_LEAF_ICON
    : depth === 0
      ? "h-5 w-5"
      : "h-3.5 w-3.5";
  const activeClass = useRailTone
    ? MODULE_RAILS.SECTION_LEAF_ON
    : "bg-sidebar-accent text-sidebar-accent-foreground shadow-elevation1";
  const inactiveClass = useRailTone
    ? MODULE_RAILS.SECTION_LEAF_OFF
    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground";
  const iconActiveClass = useRailTone
    ? MODULE_RAILS.SECTION_LEAF_ICON_ON
    : "text-sidebar-accent-foreground";
  const iconInactiveClass = useRailTone
    ? MODULE_RAILS.SECTION_LEAF_ICON_OFF
    : "text-sidebar-foreground group-hover:text-sidebar-accent-foreground";

  const prefetchHandlers = createRoutePrefetchHandlers(item.path);

  return (
    <div className="space-y-0.5">
      <Link
        to={item.path}
        className={cn(itemClassName, isBranchActive ? activeClass : inactiveClass)}
        data-testid={`nav-${item.id}`}
        onMouseEnter={prefetchHandlers.onMouseEnter}
        onFocus={prefetchHandlers.onFocus}
      >
        <Icon
          className={cn(
            iconClassName,
            "shrink-0 transition-colors",
            isBranchActive ? iconActiveClass : iconInactiveClass,
          )}
        />
        <span className="flex-1 truncate">{item.label}</span>
      </Link>
      {nestedVisible ? (
        <div className={cn("space-y-0.5", depth === 0 ? "ml-6" : "ml-4")}>
          {nested.map((nestedChild) => (
            <ModuleNavigationLink
              key={nestedChild.id}
              children={children}
              item={nestedChild}
              currentPath={currentPath}
              depth={depth + 1}
              railTone={railTone}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const NavigationItemComponent: React.FC<NavigationItemProps> = ({ item, collapsed, isActive, hasActiveChild, isOpen, currentPath, onOpen }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const Icon = getIcon(item.icon);
  const itemSelected = [isActive, hasActiveChild, isOpen].includes(true);
  const hasChildren = (item.children?.length ?? 0) > 0;
  // Content-capability L1s keep L2/L3 in ModuleRails; Platform L1 stays a single row.
  const expandable = hasChildren && !usesContentCapability(item.id);
  const showChildren = expandable && !collapsed && isOpen;
  const useModuleSections = showChildren && navChildrenUseModuleSections(item.children);
  const activeModuleSection = React.useMemo(() => activeModuleSectionId(item.children ?? [], currentPath), [item.children, currentPath]);
  const [manualModuleSectionId, setManualModuleSectionId] = React.useState<string | null>(null);
  React.useEffect(() => {
    setManualModuleSectionId(null);
  }, [currentPath, item.id]);
  const openModuleSection = manualModuleSectionId ?? activeModuleSection;
  const prefetchHandlers = createRoutePrefetchHandlers(item.path);

  if (collapsed) {
    return (
      <Link
        to={item.path}
        className={cn(
          "group relative flex items-center justify-center rounded-lg transition-all duration-200",
          SPACING.SIDEBAR_ITEM_PADDING,
          itemSelected
            ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-elevation1 before:absolute before:left-0 before:top-1/2 before:h-5 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-sidebar-primary"
            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-elevation1"
        )}
        title={item.label}
        data-testid={`nav-${item.id}`}
        onMouseEnter={prefetchHandlers.onMouseEnter}
        onFocus={prefetchHandlers.onFocus}
      >
        <Icon className="h-5 w-5" />
      </Link>
    );
  }

  return (
    <div>
      {expandable ? (
        <button
          type="button"
          className={cn(
            "group relative flex w-full items-center rounded-lg text-left text-sm font-medium transition-all duration-200",
            SPACING.SIDEBAR_ITEM_PADDING,
            SPACING.GAP_LARGE,
            itemSelected
              ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-elevation1 before:absolute before:left-0 before:top-1/2 before:h-5 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-sidebar-primary"
              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-elevation1"
          )}
          data-testid={`nav-${item.id}`}
          aria-expanded={isOpen}
          aria-controls={`nav-children-${item.id}`}
          onClick={() => onOpen(item.id)}
        >
          <Icon className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1 truncate">{item.label}</span>
          <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform", isOpen && "rotate-180")} aria-hidden />
        </button>
      ) : (
        <Link
          to={item.path}
          className={cn(
            "group relative flex items-center rounded-lg text-sm font-medium transition-all duration-200",
            SPACING.SIDEBAR_ITEM_PADDING,
            SPACING.GAP_LARGE,
            itemSelected
              ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-elevation1 before:absolute before:left-0 before:top-1/2 before:h-5 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-sidebar-primary"
              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-elevation1"
          )}
          data-testid={`nav-${item.id}`}
          onMouseEnter={prefetchHandlers.onMouseEnter}
          onFocus={prefetchHandlers.onFocus}
        >
          <Icon className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1 truncate">{item.label}</span>
        </Link>
      )}
      {showChildren ? (
        <div id={`nav-children-${item.id}`} className={cn(SPACING.SIDEBAR_CHILD_INDENT, SPACING.SIDEBAR_PARENT_CHILD_MARGIN, SPACING.SIDEBAR_CHILD_GAP)}>
          {useModuleSections ? (
            sectionIdsForChildren(item.children ?? []).map((section) => {
              const roots = rootModuleChildren(item.children ?? [], section);
              if (roots.length === 0) return null;
              const isSectionOpen = openModuleSection === section;
              const landingItem = sectionLandingItem(section, roots);
              const sectionItems = visibleSectionItems(item.children ?? [], section, landingItem);
              return (
                <div key={section} className={MODULE_RAILS.SECTION_STACK}>
                  <button
                    type="button"
                    className={cn(
                      MODULE_RAILS.SECTION_TAB,
                      isSectionOpen ? MODULE_RAILS.SECTION_TAB_ON : MODULE_RAILS.SECTION_TAB_OFF,
                    )}
                    aria-expanded={isSectionOpen}
                    onClick={() => {
                      setManualModuleSectionId(section);
                      if (landingItem !== null && !isPathActive(currentPath, landingItem.path)) {
                        navigate(landingItem.path);
                      }
                    }}
                  >
                    <span className="flex-1 truncate uppercase">{resolveNavSectionLabel(t, section)}</span>
                    <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform", isSectionOpen && "rotate-180")} aria-hidden />
                  </button>
                  {isSectionOpen ? (
                    <div className="space-y-0.5">
                      {sectionItems.map((child) => (
                        <ModuleNavigationLink
                          key={child.id}
                          children={item.children ?? []}
                          item={child}
                          currentPath={currentPath}
                          depth={0}
                          railTone="rail"
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })
          ) : (
            sidebarRootChildren(item.children ?? []).map((child) => (
              <ModuleNavigationLink
                key={child.id}
                children={item.children ?? []}
                item={child}
                currentPath={currentPath}
                depth={0}
                railTone="rail"
              />
            ))
          )}
        </div>
      ) : null}
    </div>
  );
};

function canShowNavigationItem(
  item: NavigationItem,
  checkPermission: (permission: string) => boolean,
  permissionsKnown: boolean,
): boolean {
  const policy = getSidebarRoutePolicy(item.path);
  const requiredPermissions = policy?.requiredPermissions ?? item.requiredPermissions;
  if (!requiredPermissions) return true;
  if (requiredPermissions.length === 0) return true;
  if (!permissionsKnown) return false;
  return policy?.permissionMatch === "all"
    ? requiredPermissions.every(checkPermission)
    : requiredPermissions.some(checkPermission);
}

function filterNavigationItems(
  items: NavigationItem[],
  checkPermission: (permission: string) => boolean,
  permissionsKnown: boolean,
): NavigationItem[] {
  const visible: NavigationItem[] = [];
  for (const item of items) {
    const rawChildren = item.children;
    // Shell config sets children: [] on leaves — empty array is a leaf, not an empty group.
    if (!Array.isArray(rawChildren) || rawChildren.length === 0) {
      if (!canShowNavigationItem(item, checkPermission, permissionsKnown)) continue;
      visible.push(Array.isArray(rawChildren) ? { ...item, children: undefined } : item);
      continue;
    }
    const children = filterNavigationItems(rawChildren, checkPermission, permissionsKnown);
    if (children.length === 0) continue;
    visible.push({ ...item, path: children[0].path, children });
  }
  return visible;
}

function itemContainsPath(item: NavigationItem, currentPath: string): boolean {
  if (isPathActive(currentPath, item.path)) return true;
  return Boolean(item.children?.some((child) => itemContainsPath(child, currentPath)));
}

function findActiveParentId(items: NavigationItem[], currentPath: string): string | null {
  const activeParent = items.find((item) => (item.children?.length ?? 0) > 0 && itemContainsPath(item, currentPath));
  return activeParent?.id ?? null;
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { navigationItems, appConfig, loading, error } = useFrontendConfig();
  const { checkPermission, refreshPolicySnapshot, state: securityState } = useSecurityContext();
  const permissionsVersion = Array.isArray(securityState.permissions) ? securityState.permissions.join("\u001f") : "";
  const localizedNavigationItems = React.useMemo(() => localizeNavigationItems(navigationItems, t), [navigationItems, t]);
  const visibleNavigationItems = React.useMemo(() => {
    void permissionsVersion;
    return filterNavigationItems(localizedNavigationItems, checkPermission, securityState.policySnapshotReady);
  }, [localizedNavigationItems, checkPermission, permissionsVersion, securityState.policySnapshotReady]);
  const [policyRetrying, setPolicyRetrying] = React.useState(false);
  const navAwaitingPolicy =
    !loading &&
    !error &&
    localizedNavigationItems.length > 0 &&
    !securityState.policySnapshotReady &&
    !securityState.policySnapshotError;
  const showNavLoading = loading ? true : navAwaitingPolicy;
  const showPolicyError =
    !loading && !error && !showNavLoading && securityState.policySnapshotError === true;
  const retryPolicySnapshot = React.useCallback(async () => {
    if (policyRetrying) return;
    setPolicyRetrying(true);
    try {
      await refreshPolicySnapshot({ force: true, swr: false });
    } finally {
      setPolicyRetrying(false);
    }
  }, [policyRetrying, refreshPolicySnapshot]);
  const activeParentId = React.useMemo(() => findActiveParentId(visibleNavigationItems, location.pathname), [visibleNavigationItems, location.pathname]);
  const [manualOpenParentId, setManualOpenParentId] = React.useState<string | null>(null);
  React.useEffect(() => {
    setManualOpenParentId(null);
  }, [location.pathname]);
  const openParentId = manualOpenParentId ?? activeParentId;
  const appName = configText(appConfig?.name, "Nice Solutions");
  const appVersion = configText(appConfig?.version, "2.0.0");

  const isItemActive = (item: NavigationItem): boolean => {
    return itemContainsPath(item, location.pathname);
  };

  const hasActiveChild = (item: NavigationItem): boolean => {
    if (!item.children) return false;
    return item.children.some((child) => itemContainsPath(child, location.pathname));
  };

  return (
    <div
      className={cn(
        APP_SHELL.sidebarAside,
        collapsed ? APP_SHELL.sidebarAsideCollapsed : APP_SHELL.sidebarAsideExpanded,
      )}
    >
      <div
        className={cn(
          "flex h-14 shrink-0 items-center border-b border-sidebar-border",
          collapsed ? "justify-center px-2" : "justify-between px-3",
        )}
      >
        {!collapsed ? (
          <>
            <div className="flex min-w-0 items-center gap-2">
              <div className={APP_SHELL.sidebarBrandMark}>
                <BrandMark compact testId="sidebar-brand-logo" />
              </div>
              <span className={APP_SHELL.sidebarBrandName}>{appName}</span>
            </div>
            <button
              onClick={onToggle}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg hover:bg-sidebar-accent focus:outline-none focus:ring-2 focus:ring-sidebar-ring"
              aria-label={t("common.collapseSidebar")}
            >
              <ChevronLeft className="h-4 w-4 text-sidebar-foreground" />
            </button>
          </>
        ) : (
          <button
            onClick={onToggle}
            className="flex h-10 w-10 items-center justify-center rounded-lg hover:bg-sidebar-accent focus:outline-none focus:ring-2 focus:ring-sidebar-ring"
            aria-label={t("common.expandSidebar")}
          >
            <div className={cn(APP_SHELL.sidebarBrandMark, "h-8 w-8")}>
              <BrandMark compact testId="sidebar-brand-logo" />
            </div>
          </button>
        )}
      </div>

      <nav
        className={cn("flex-1 overflow-y-auto", SPACING.SIDEBAR_NAV_GAP, SPACING.SIDEBAR_NAV_PADDING, "flex flex-col")}
        aria-label={t("common.mainNavigation")}
      >
        {showNavLoading ? (
          <div className={cn("flex flex-col", SPACING.SIDEBAR_NAV_GAP)} role="status" data-testid="sidebar-nav-loading">
            {Array.from({ length: 8 }, (_, index) => (
              <div
                key={`nav-skeleton-${index}`}
                className={cn(
                  "h-9 animate-pulse rounded-lg bg-sidebar-accent/60",
                  collapsed ? "mx-auto w-9" : "w-full",
                )}
              />
            ))}
            <span className="sr-only">{t("common.loading")}</span>
          </div>
        ) : error ? (
          <div className={cn("text-center", SPACING.EMPTY_STATE_PADDING)}>
            <p className={cn("text-sm text-destructive", SPACING.MARGIN_BOTTOM)}>{t("common.navLoadFailed")}</p>
            <p className={SECTION_SUBTITLE}>{error}</p>
          </div>
        ) : showPolicyError ? (
          <div
            className={cn("flex flex-col items-center text-center", SPACING.EMPTY_STATE_PADDING, SPACING.SIDEBAR_NAV_GAP)}
            data-testid="sidebar-policy-error"
            role="alert"
          >
            <p className={cn("text-sm text-destructive", SPACING.MARGIN_BOTTOM)}>{t("common.navLoadFailed")}</p>
            <p className={SECTION_SUBTITLE}>{t("common.loadingPlatformPermissions")}</p>
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-xs font-medium text-sidebar-foreground hover:bg-sidebar-accent focus:outline-none focus:ring-2 focus:ring-sidebar-ring disabled:opacity-60"
              onClick={() => {
                void retryPolicySnapshot();
              }}
              disabled={policyRetrying}
              data-testid="sidebar-policy-retry"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", policyRetrying ? "animate-spin" : undefined)} aria-hidden />
              {t("common.retry")}
            </button>
          </div>
        ) : (
          visibleNavigationItems.map((item) => (
            <NavigationItemComponent
              key={item.id}
              item={item}
              collapsed={collapsed}
              isActive={isItemActive(item)}
              hasActiveChild={hasActiveChild(item)}
              isOpen={openParentId === item.id}
              currentPath={location.pathname}
              onOpen={setManualOpenParentId}
            />
          ))
        )}
      </nav>

      <div className="border-t border-sidebar-border px-3 py-2">
        {!collapsed && (
          <div className="space-y-0.5 text-[10px] text-muted-foreground dark:text-sidebar-foreground/65">
            <p className="truncate">{appName} Platform</p>
            <p>Version {appVersion}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export { canShowNavigationItem, filterNavigationItems };
export type { NavigationItem as SidebarNavigationItem };

export default Sidebar;
